import React, {useCallback, useMemo, useRef, useState} from 'react';
import {FlatList, View, TouchableOpacity, Pressable} from 'react-native';
import Text from '../../elements/Text';
import RecommendedResourceCard from '../RecommendedResourceCard';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {useNavigate} from '../../../utils/mockedWebLibs/react-router-dom';
import {tID} from '../../../utils/TestIdHelper';
import CommonStyles from '../../../utils/CommonStyles';
import {LeftArrowIcon, RightArrowIcon} from '../../../utils/SvgIcons';

const RecomendedResourcesList = ({data, refreshMainList, title, isSeeAll}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const recommendedResourcesListRef = useRef();

  const [viewableItems, setViewableItems] = useState([]);

  const navigate = useNavigate();

  const _onViewableItemsChanged = useCallback(({viewableItems, changed}) => {
    setViewableItems(viewableItems);
  }, []);

  const _viewabilityConfig = {
    itemVisiblePercentThreshold: 90,
  };

  const nextScrollIndex = useMemo(() => {
    return viewableItems?.[viewableItems?.length - 1]?.index;
  }, [viewableItems]);

  const previousScrollIndex = useMemo(() => {
    return viewableItems?.[0]?.index;
  }, [viewableItems]);

  const onNextPress = () => {
    if (nextScrollIndex !== data?.length - 1) {
      recommendedResourcesListRef?.current?.scrollToIndex({
        index: nextScrollIndex + 1,
      });
    }
  };

  const onPreviousPress = () => {
    if (previousScrollIndex !== 0) {
      recommendedResourcesListRef?.current?.scrollToIndex({
        index: previousScrollIndex - 1,
      });
    }
  };

  const renderRecommendedItem = ({item, index}) => {
    return (
      <RecommendedResourceCard
        navigation={navigate}
        item={item}
        index={index}
        refreshMainList={refreshMainList}
      />
    );
  };

  return (
    <>
      <View
        testID={tID('RecomendedResourcesList-View-TitleContainer')}
        style={isSeeAll && styles.seeAllView}>
        <Text
          testID={tID('RecomendedResourcesList-Text-Title')}
          variation="Headline4SemiBold"
          color={colors.textHeading}>
          {title || 'Recommended Resources'}
        </Text>
        {isSeeAll && (
          <TouchableOpacity
            testID={tID('RecomendedResourcesList-TouchableOpacity-SeeAll')}
            onPress={() => navigate('/resources')}>
            <Text
              testID={tID('RecomendedResourcesList-Text-SeeAll')}
              style={styles.seeAllText}
              lineHeight={21}>
              See All
            </Text>
          </TouchableOpacity>
        )}
      </View>
      <FlatList
        testID={tID('RecomendedResourcesList-FlatList-Resources')}
        ref={recommendedResourcesListRef}
        horizontal
        data={data}
        showsHorizontalScrollIndicator={false}
        renderItem={renderRecommendedItem}
        keyExtractor={(item, index) => String(item.id || index)}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.recommendedListContainer}
        onViewableItemsChanged={_onViewableItemsChanged}
        viewabilityConfig={_viewabilityConfig}
      />
      {data?.length &&
      (previousScrollIndex !== 0 || nextScrollIndex !== data?.length - 1) ? (
        <View
          testID={tID('RecomendedResourcesList-View-ArrowsContainer')}
          style={[
            styles.webArrows,
            title === 'Recommended Resources' && CommonStyles.mb_32,
          ]}>
          <Pressable
            onClick={onPreviousPress}
            disabled={previousScrollIndex === 0}
            style={{
              ...styles.arrowBg,
              ...(previousScrollIndex === 0 && styles.arrowDisabled),
            }}>
            <LeftArrowIcon
              testID={tID('RecomendedResourcesList-img-PreviousArrow')}
              width={8}
              height={14}
            />
          </Pressable>
          <Pressable
            onClick={onNextPress}
            disabled={nextScrollIndex === data?.length - 1}
            style={{
              ...styles.arrowBg,
              ...(nextScrollIndex === data?.length - 1 && styles.arrowDisabled),
            }}>
            <RightArrowIcon
              testID={tID('RecomendedResourcesList-img-NextArrow')}
              width={8}
              height={14}
            />
          </Pressable>
        </View>
      ) : null}
    </>
  );
};

export default RecomendedResourcesList;
